import { I18n } from "@assets/strings";
import { Set } from "immutable";
import { RefObject, useEffect, useRef, useState } from "react";
import { SimpleTextInputProps } from "./simpleTextInput";

interface FormUtils<T> {
	fieldProps: (field: keyof T) => SimpleTextInputProps;
	checkSubmit: () => boolean;
	formValue: T;
	setValue: (newValue: T) => void;
}
export function useForm<T extends { [key: string]: string }>(
	initialValue: T,
	mandatories?: (keyof T)[],
	validityMap?: { [key in keyof T]?: (fieldValue: string, otherValues: T) => boolean },
	invalidMessages?: { [key in keyof T]?: string }
): FormUtils<T> {
	const [value, setValue] = useState(initialValue);
	const [missingFields, setMissingFields] = useState<Set<keyof T>>(Set());
	const [invalidFields, setInvalidFields] = useState<Set<keyof T>>(Set());

	const fieldProps = (field: keyof T): SimpleTextInputProps => ({
		value: value[field] ?? "",
		onChange: (v) => {
			setValue((c) => ({ ...c, [field]: v.replace(/[\u200E]/g, "") }));
			setMissingFields((c) => c.delete(field));
			setInvalidFields((c) => c.delete(field));
		},
		onFocus: () => {
			setMissingFields((c) => c.delete(field));
			setInvalidFields((c) => c.delete(field));
		},
		error: missingFields.has(field)
			? I18n.t("form.missing")
			: invalidFields.has(field)
				? (invalidMessages?.[field] ?? I18n.t("form.invalid"))
				: "",
	});

	const checkSubmit = (): boolean => {
		const fields = Object.keys(value);
		const missing = Set(fields.filter((field) => mandatories?.includes(field) && !value[field]));
		const invalid = Set(
			fields.filter((field) => {
				const checkFunction = validityMap?.[field];
				return checkFunction && !checkFunction(value[field], value);
			})
		);
		setMissingFields(missing);
		setInvalidFields(invalid);

		return missing.size === 0 && invalid.size === 0;
	};

	return {
		fieldProps,
		checkSubmit,
		formValue: value,
		setValue,
	};
}

export function useIsTyping(): boolean {
	const [typing, setTyping] = useState(false);
	const typingTimeout = useRef<NodeJS.Timeout>(null);

	useEffect(() => {
		const listener = (): void => {
			setTyping(true);
			if (typingTimeout.current) {
				clearTimeout(typingTimeout.current);
			}
			typingTimeout.current = setTimeout(() => setTyping(false), 400);
		};

		document.addEventListener("keydown", listener);
		return () => document.removeEventListener("keydown", listener);
	}, []);

	return typing;
}

export function useSliderRange(value: number): { ref: RefObject<HTMLInputElement | null>; range: number } {
	const [range, setRange] = useState(value);
	const ref = useRef<HTMLInputElement>(null);

	useEffect(() => {
		if (ref.current) {
			const target = ref.current;
			const { max, min, value } = target;
			setRange(((+value - +min) * 100) / (+max - +min));
		}
	}, [value]);

	return { range, ref };
}
